import React from 'react';

import { Box, Button, Divider, Icon } from '@rbilabs/universal-components';
import { getCartEntryId, getHydratedEntityId } from 'features/cart/utils/minimal-cart';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { DebugToolModal } from 'components/debug-tool';
import { Typography } from 'components/ui';
import { MenuObjectTypes } from 'enums/menu';
import { IPriceDto } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';

import { SelectedItemDebug } from '../components/selected-item-debug';

const getCartEntryAsMenuObjectType = (cartEntry: ILegacyCartEntry) => {
  return cartEntry.type === CartEntryType.combo || cartEntry.type === CartEntryType.offerCombo
    ? MenuObjectTypes.COMBO
    : MenuObjectTypes.ITEM;
};

const getComboSlotContextualPrice = ({
  cartEntry,
  comboSlot,
  selectedEntry,
  indexedMenu,
}: {
  cartEntry: ILegacyCartEntry;
  comboSlot: ILegacyCartEntry;
  selectedEntry: ILegacyCartEntry;
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
}): IPriceDto | undefined => {
  const comboSlotId = getCartEntryId(comboSlot);
  const contextualComboSlotId = comboSlotId === cartEntry._id ? 'main' : comboSlotId;
  const oneMenuComboSlot = indexedMenu.find(
    getHydratedEntityId(contextualComboSlotId, cartEntry._id)
  );

  return (
    oneMenuComboSlot?.options?.entries?.find(
      option => option.entryId === getCartEntryId(selectedEntry)
    )?.price ?? undefined
  );
};

const SelectedCartEntryComboSlotsDebug = ({ cartEntry }: { cartEntry: ILegacyCartEntry }) => {
  const { indexedMenu } = useUnifiedMenuContext();
  const comboSlots = cartEntry.children
    .filter(
      (child: ILegacyCartEntry) =>
        (child.type === CartEntryType.comboSlot || child.type === CartEntryType.item) &&
        child.uiPattern !== ComboSlotUiPattern.HIDDEN
    )
    .flatMap((comboSlot: ILegacyCartEntry) => {
      return comboSlot.type === CartEntryType.item ? (
        <React.Fragment key={comboSlot._id}>
          <Divider />
          <SelectedItemDebug
            selectedItemVendorConfig={comboSlot}
            productName={comboSlot.name}
            selectedItemType={getCartEntryAsMenuObjectType(comboSlot)}
            oneMenuEntryId={getCartEntryId(comboSlot)}
            hideTitle
            hidePosVendor
          />
        </React.Fragment>
      ) : (
        comboSlot.children.map((selectedEntry: ILegacyCartEntry) => {
          const selectedEntryType = getCartEntryAsMenuObjectType(selectedEntry);

          return selectedEntry ? (
            <React.Fragment key={selectedEntry._id}>
              <Divider />
              <SelectedItemDebug
                selectedItemVendorConfig={selectedEntry}
                productName={selectedEntry.name}
                selectedItemType={selectedEntryType}
                oneMenuEntryId={getCartEntryId(selectedEntry)}
                contextualPrice={getComboSlotContextualPrice({
                  cartEntry,
                  comboSlot,
                  selectedEntry,
                  indexedMenu,
                })}
                hideTitle
                hidePosVendor
              />
            </React.Fragment>
          ) : null;
        })
      );
    });

  return (
    <Box marginTop="$4">
      <Typography variant="headerOne">Combo Slots:</Typography>
      {comboSlots}
    </Box>
  );
};

const CartDebugModal: React.FC<{
  cartEntry: ILegacyCartEntry;
  onClose: () => void;
}> = ({ cartEntry, onClose }) => {
  const isCombo =
    cartEntry.type === CartEntryType.combo || cartEntry.type === CartEntryType.offerCombo;

  return (
    <DebugToolModal onOpenEventMessage="Menu Debug Tool: Cart Item" onClose={onClose}>
      <SelectedItemDebug
        selectedItemVendorConfig={cartEntry}
        productName={cartEntry.name}
        selectedItemType={getCartEntryAsMenuObjectType(cartEntry)}
        oneMenuEntryId={getCartEntryId(cartEntry)}
      />
      <Divider />
      {isCombo && <SelectedCartEntryComboSlotsDebug cartEntry={cartEntry} />}
    </DebugToolModal>
  );
};

export const CartDebugMenu = ({ cartEntry }: { cartEntry?: ILegacyCartEntry }) => {
  const [isDebugMenuModalOpen, setIsDebugMenuModalOpen] = React.useState(false);

  return (
    <>
      <Button
        variant="ghost"
        onPress={() => setIsDebugMenuModalOpen(true)}
        leftIcon={<Icon name="mi:bug-report" />}
      >
        <Typography>View Product info</Typography>
      </Button>

      {cartEntry && isDebugMenuModalOpen ? (
        <CartDebugModal onClose={() => setIsDebugMenuModalOpen(false)} cartEntry={cartEntry} />
      ) : null}
    </>
  );
};
